import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  AttachMoney as AttachMoneyIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  BeachAccess as BeachAccessIcon,
  Schedule as ScheduleIcon,
  Folder as FolderIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

import heroImg from '../assets/home.jpg'

const featuresBase = [
  { id: 'employee', title: 'Employee Management', description: 'Track employee details, history, and performance.', icon: <PeopleIcon /> },
  { id: 'recruitment', title: 'Recruitment and Onboarding', description: 'Post jobs, track applicants, and onboard new hires.', icon: <WorkIcon /> },
  { id: 'payroll', title: 'Payroll and Compensation', description: 'Automatically calculate salaries, taxes, and benefits.', icon: <AttachMoneyIcon /> },
  { id: 'learning', title: 'Training and Development', description: 'Plan and deliver employee training programs.', icon: <SchoolIcon /> },
  { id: 'performance', title: 'Performance Management', description: 'Set goals, review performance, and give feedback.', icon: <AssessmentIcon /> },
  { id: 'leave', title: 'Leave Management', description: 'Manage employee leave requests easily.', icon: <BeachAccessIcon /> },
  { id: 'attendance', title: 'Attendance Management', description: 'Monitor work hours and track overtime.', icon: <ScheduleIcon /> },
  { id: 'projects', title: 'Project Management', description: 'Plan and track HR projects, tasks, and team collaboration.', icon: <FolderIcon /> }
];

const HomePage = ({ topOffset = 0, navigateFn }) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');

  const navigate = (path) => {
    if (typeof navigateFn === 'function') return navigateFn(path);
    if (typeof window !== 'undefined') window.location.href = path;
  };

  const features = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return featuresBase;
    return featuresBase.filter(
      (f) =>
        f.title.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q) ||
        f.id.toLowerCase().includes(q)
    );
  }, [query]);

  const handleCardClick = (id) => navigate('/login');
  const handleGetStarted = () => navigate('/login');

  return (
    <Box sx={{ minHeight: '100%'}}>
      <Container maxWidth="xl" >
        
        {/* HERO SECTION */}
        <Grid container spacing={5} alignItems="center" >
          <Grid item xs={12} md={7}>
            <Box sx={{ maxWidth: 680 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 900,
                  mb: 2.5,
                  letterSpacing: '-0.03em',
                  fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
                  textAlign: { xs: 'center', md: 'left' },
                  background: 'black',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2
                }}
              >
                Welcome to Pulse HR
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  textAlign: { xs: 'center', md: 'left' },
                  lineHeight: 1.7,
                  fontWeight: 400
                }}
              >
                Easily handle all HR tasks like managing employee details, training, leave, attendance, payroll and more —
                from one secure, modern platform built for teams.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 4 }}
                alignItems="center"
                justifyContent={{ xs: 'center', md: 'flex-start' }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    bgcolor: '#1976d2',
                    boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
                    textTransform: 'none',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#1565c0',
                      boxShadow: '0 6px 30px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                  }}
                >
                  Get Started
                </Button>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <Card
              elevation={0}
              sx={{
                width: { xs: '95%', sm: 460, md: 500 },
                height: { xs: 280, sm: 340 },
                borderRadius: 4,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.12)'
                }
              }}
            >
              <Box
                component="img"
                src={heroImg}
                alt="Pulse HR dashboard preview"
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </Card>
          </Grid>
        </Grid>

        {/* FEATURES SECTION */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '1.5rem', md: '2rem' },
                color: 'black'
              }}
            >
              Core HR Capabilities
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feat, index) => (
              <Grid key={feat.id} item xs={12} sm={6} md={3}>
                <Paper
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleCardClick(feat.id);
                  }}
                  onClick={() => handleCardClick(feat.id)}
                  elevation={0}
                  sx={{
                    p: 3,
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#1976d2', 0.1),
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)',
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 24px 48px rgba(25, 118, 210, 0.15)',
                      borderColor: alpha('#1976d2', 0.3),
                      '&::before': {
                        transform: 'scaleX(1)'
                      }
                    }
                  }}
                >
             <Stack
  direction="column"
  spacing={2.5}
  alignItems="center"
  textAlign="center"
>
  <Avatar
    sx={{
      bgcolor: alpha('#1976d2', 0.1),
      color: '#1976d2',
      width: 64,
      height: 64,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 16px rgba(25, 118, 210, 0.15)',
      '& svg': {
        fontSize: '1.75rem',
      },
    }}
  >
    {feat.icon}
  </Avatar>

  <Box>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        mb: 1,
        fontSize: '1.05rem',
        color: '#212121',
      }}
    >
      {feat.title}
    </Typography>

    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        lineHeight: 1.6,
        fontSize: '0.9rem',
      }}
    >
      {feat.description}
    </Typography>
  </Box>
</Stack>

                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FOOTER */}
        <Box sx={{ mt: 6, pb: 4 }}>
          <Divider />
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between" 
            sx={{ mt: 3, gap: 2 }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              © {new Date().getFullYear()} PulseHR · Bridgeme Technologies Private Limited
            </Typography>
            <Stack direction="row" spacing={3}>
              <Link 
                to="/privacy" 
                style={{ textDecoration: 'none', color: '#64748b', fontSize: '0.875rem', fontWeight: 500 }}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                style={{ textDecoration: 'none', color: '#64748b', fontSize: '0.875rem', fontWeight: 500 }}
              >
                Terms of Use
              </Link>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;